import React, { useMemo } from "react";
import { fmtINR, fmtDate } from "../lib/apiClient";

/**
 * Tally-format GST Tax Invoice / Document print template.
 * Single thin black border surrounds everything; internal vertical separators.
 * Watermarks: two equipment images centred (light opacity, behind text) for sales docs.
 */

// Indian rupee → words (handles up to crores)
function numToWords(num) {
  if (num === undefined || num === null || isNaN(num)) return "Zero";
  num = Math.abs(Math.round(Number(num) * 100) / 100);
  const a = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
  const b = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
  function below1000(n) {
    let str = "";
    if (n >= 100) { str += a[Math.floor(n / 100)] + " Hundred "; n %= 100; }
    if (n >= 20) { str += b[Math.floor(n / 10)] + " "; n %= 10; if (n) str += a[n] + " "; }
    else if (n) str += a[n] + " ";
    return str.trim();
  }
  const intPart = Math.floor(num);
  const paise = Math.round((num - intPart) * 100);
  let n = intPart;
  let words = "";
  const crore = Math.floor(n / 10000000); n %= 10000000;
  const lakh = Math.floor(n / 100000); n %= 100000;
  const thousand = Math.floor(n / 1000); n %= 1000;
  if (crore) words += below1000(crore) + " Crore ";
  if (lakh) words += below1000(lakh) + " Lakh ";
  if (thousand) words += below1000(thousand) + " Thousand ";
  if (n) words += below1000(n);
  words = words.trim();
  if (!words) words = "Zero";
  let out = `INR ${words}`;
  if (paise > 0) out += ` and ${below1000(paise)} Paise`;
  out += " Only";
  return out;
}

const COLS_TAX = ["Sl", "Description of Goods", "HSN/SAC", "Quantity", "Rate", "per", "Disc%", "Amount"];
const COLS_NOTAX = ["Sl", "Description of Goods", "HSN/SAC", "Quantity", "Rate", "per", "Amount"];

const FALLBACK_LOGO = "/equipment/hans-logo.jpeg";

const STANDARD_TERMS = [
  "Sales Goods Are Not Returnable.",
  "Our Responsibility Ceases After the Goods Have Left Our Premises.",
  "Guarantee Against Manufacturing Defect for 6 Months.",
  "Disputes Are Subject to MEERUT Jurisdiction Only.",
  "We Are Closed on Sunday.",
];

export default function TallyInvoicePrint({ doc, company, party, cfg, docTypeLabel }) {
  const showTax = !!cfg?.showTax;
  const isInter = !!doc.is_inter_state;
  const items = doc.items || [];

  // HSN + GST-rate wise tax summary (separate row per rate so mixed-rate invoices show each % distinctly)
  const hsnMap = {};
  for (const it of items) {
    const hsn = (it.hsn || "").trim() || "—";
    const r = Number(it.gst_rate || 0);
    const key = `${hsn}|${r}`;
    if (!hsnMap[key]) hsnMap[key] = { hsn, taxable: 0, cgst_rate: 0, cgst: 0, sgst_rate: 0, sgst: 0, igst_rate: 0, igst: 0 };
    hsnMap[key].taxable += Number(it.taxable_amount || 0);
    hsnMap[key].cgst += Number(it.cgst_amount || 0);
    hsnMap[key].sgst += Number(it.sgst_amount || 0);
    hsnMap[key].igst += Number(it.igst_amount || 0);
    if (isInter) hsnMap[key].igst_rate = r;
    else { hsnMap[key].cgst_rate = r / 2; hsnMap[key].sgst_rate = r / 2; }
  }
  const hsnRows = Object.values(hsnMap).sort((a, b) => a.hsn.localeCompare(b.hsn) || (a.cgst_rate + a.igst_rate) - (b.cgst_rate + b.igst_rate));

  // Hide advance amount on the printed Tax Invoice (compliance requirement).
  // Advance is still captured in the doc + other printouts (e.g. Sales Order).
  const showAdvanceOnPrint = cfg?.showAdvance
    && doc.doc_type !== "invoice"
    && Number(doc.advance_amount || 0) > 0;

  const grandPayable = (() => {
    if (showAdvanceOnPrint) {
      return Math.max(0, Number(doc.grand_total || 0) - Number(doc.advance_amount || 0));
    }
    return Number(doc.grand_total || 0);
  })();

  // Memoize the party address-line — runs in two table cells below
  // so a single useMemo avoids two .filter().join() passes per render.
  const partyAddressLine = useMemo(
    () => [party.city, party.state, party.pincode].filter(Boolean).join(", "),
    [party.city, party.state, party.pincode]
  );

  return (
    <div className="tally-print-root relative bg-white text-[11px] leading-tight text-black border-2 border-black">
      {/* Watermark — company logo at low opacity, centred, behind content.
          Uses the live company.logo_url with fallback to bundled hans-logo.jpeg
          so it shows even on un-themed installs / public share pages. */}
      <div aria-hidden className="watermark-layer pointer-events-none absolute inset-0 flex items-center justify-center z-0 select-none">
        <img
          src={(company.logo_url || "").trim() || FALLBACK_LOGO}
          alt=""
          crossOrigin="anonymous"
          onError={(e) => { e.currentTarget.src = FALLBACK_LOGO; }}
          className="w-[55%] max-w-[520px] h-auto object-contain"
          style={{ opacity: 0.08 }}
        />
      </div>

      <div className="relative z-10">
        {/* Title strip */}
        <div className="flex items-stretch border-b border-black">
          <div className="w-1/2 p-2.5 border-r border-black">
            <div className="flex items-start gap-2">
              {(() => {
                // Some uploads landed in DB as a 1×1 placeholder data URI from the visual editor — treat those as empty
                const stored = (company.logo_url || "").trim();
                const looksValid = stored && !(stored.startsWith("data:") && stored.length < 500);
                const logoSrc = looksValid ? stored : FALLBACK_LOGO;
                return (
                  <img
                    src={logoSrc}
                    alt="company-logo"
                    crossOrigin="anonymous"
                    className="w-14 h-14 border border-black/10 rounded-sm bg-white object-contain flex-shrink-0"
                    onError={(e) => { e.currentTarget.src = FALLBACK_LOGO; }}
                  />
                );
              })()}
              <div>
                <div className="font-display text-[15px] font-bold uppercase tracking-tight">{company.legal_name}</div>
                <div className="text-[10px] leading-snug">{company.address_line}{company.city ? `, ${company.city}` : ""}{company.state ? `, ${company.state}` : ""}{company.pincode ? ` - ${company.pincode}` : ""}</div>
                <div className="text-[10px]"><b>Mfg. of:</b> Agriculture Implements & Their Parts</div>
                <div className="text-[10px]"><b>Contact:</b> {company.phone || "—"}{company.email ? `   E-Mail: ${company.email}` : ""}</div>
                <div className="text-[10px]"><b>GSTIN:</b> <span className="font-mono">{company.gstin}</span>   <b>State Name:</b> {company.state}, Code: {company.state_code}</div>
              </div>
            </div>
          </div>
          <div className="w-1/2 flex flex-col">
            <div className="text-center font-display text-[18px] font-bold uppercase tracking-wide py-1.5 border-b border-black">{(docTypeLabel || cfg?.label || "TAX INVOICE").toUpperCase()}</div>
            <div className="grid grid-cols-2 text-[10px] flex-1">
              <Cell label={`${(docTypeLabel || cfg?.label || "Invoice")} No.`} value={doc.doc_number} />
              <Cell label="Date" value={fmtDate(doc.doc_date)} />
              <Cell label="e-Way Bill No." value={doc.eway_bill_no || "—"} />
              <Cell label="Vehicle No." value={doc.vehicle_no || "—"} />
              <Cell label="Transport" value={doc.transporter_name || "—"} />
              <Cell label="L.R. No." value={doc.transporter_id || "—"} />
              <Cell label="Reference" value={doc.reference || "—"} />
              <Cell label="Place of Supply" value={`${doc.place_of_supply_state_code || "—"}`} last />
            </div>
          </div>
        </div>

        {/* Buyer + Consignee */}
        <div className="flex border-b border-black">
          <div className="w-1/2 p-2.5 border-r border-black">
            <div className="text-[10px] font-bold uppercase mb-1">Details of Receiver (Billed to)</div>
            <div className="font-bold text-[12px]">{party.name || "—"}</div>
            <div className="text-[10px] leading-snug whitespace-pre-line">{party.billing_address || ""}</div>
            <div className="text-[10px]">{partyAddressLine}</div>
            {party.phone && <div className="text-[10px]"><b>Contact:</b> {party.phone}</div>}
            {party.email && <div className="text-[10px]"><b>E-Mail:</b> {party.email}</div>}
            <div className="text-[10px]"><b>GSTIN:</b> <span className="font-mono">{party.gstin || "URP"}</span></div>
            <div className="text-[10px]"><b>State Name:</b> {party.state || "—"}{party.state_code ? `, Code: ${party.state_code}` : ""}</div>
          </div>
          <div className="w-1/2 p-2.5">
            <div className="text-[10px] font-bold uppercase mb-1">Details of Consignee (Shipped to)</div>
            <div className="font-bold text-[12px]">{party.name || "—"}</div>
            <div className="text-[10px] leading-snug whitespace-pre-line">{party.shipping_address || party.billing_address || ""}</div>
            <div className="text-[10px]">{partyAddressLine}</div>
            <div className="text-[10px]"><b>GSTIN:</b> <span className="font-mono">{party.gstin || "URP"}</span></div>
            <div className="text-[10px]"><b>State Name:</b> {party.state || "—"}{party.state_code ? `, Code: ${party.state_code}` : ""}</div>
            <div className="text-[10px]"><b>Place of Supply:</b> {party.state || "—"}</div>
          </div>
        </div>

        {/* Optional meta strip (expected delivery / serial / advance) */}
        {(doc.expected_delivery_date || doc.expected_return_date || doc.serial_number || doc.item_make_model || doc.purpose_of_job_work || showAdvanceOnPrint) && (
          <div className="grid grid-cols-4 text-[10px] border-b border-black">
            {doc.expected_delivery_date && <MetaCell k="Expected Delivery" v={fmtDate(doc.expected_delivery_date)} />}
            {doc.expected_return_date && <MetaCell k="Expected Return" v={fmtDate(doc.expected_return_date)} />}
            {doc.item_make_model && <MetaCell k="Item / Make-Model" v={doc.item_make_model} />}
            {doc.serial_number && <MetaCell k="Serial No." v={doc.serial_number} mono />}
            {doc.purpose_of_job_work && <MetaCell k="Purpose" v={doc.purpose_of_job_work} />}
            {showAdvanceOnPrint && <MetaCell k="Advance Received" v={`${fmtINR(doc.advance_amount)}${doc.advance_mode ? ` · ${doc.advance_mode}` : ""}`} />}
          </div>
        )}

        {/* Line items table */}
        <table className="w-full text-[10px] tally-items-table">
          <colgroup>
            <col style={{ width: "4%" }} />
            <col style={{ width: showTax ? "36%" : "41%" }} />
            <col style={{ width: "10%" }} />
            <col style={{ width: "11%" }} />
            <col style={{ width: "11%" }} />
            <col style={{ width: "6%" }} />
            {showTax && <col style={{ width: "7%" }} />}
            <col style={{ width: "15%" }} />
          </colgroup>
          <thead>
            <tr className="bg-[#FAF6E8]">
              {(showTax ? COLS_TAX : COLS_NOTAX).map((h) => (
                <th key={h} className={`font-bold text-left ${["Quantity", "Rate", "Disc%", "Amount"].includes(h) ? "!text-right" : ""}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((it, i) => (
              <tr key={`${it.hsn || "noh"}-${it.name || "n"}-${i}`} className="align-top">
                <td className="text-center">{i + 1}</td>
                <td>
                  <div className="font-semibold">{it.name}</div>
                  {it.description && <div className="text-[9px] leading-snug">{it.description}</div>}
                </td>
                <td className="font-mono">{it.hsn || "—"}</td>
                <td className="text-right tabular">{Number(it.quantity || 0).toFixed(2)} {it.unit || ""}</td>
                <td className="text-right tabular">{fmtINR(it.rate)}</td>
                <td>{it.unit || "NOS"}</td>
                {showTax && <td className="text-right tabular">{Number(it.discount_pct || 0) > 0 ? Number(it.discount_pct).toFixed(2) : ""}</td>}
                <td className="text-right tabular font-semibold">{fmtINR(it.taxable_amount)}</td>
              </tr>
            ))}
            {/* Filler empty row so table stretches to fill the page on screen */}
            <tr className="filler-row">
              <td className="text-center text-transparent">.</td>
              <td className="text-transparent">.</td>
              <td className="text-transparent">.</td>
              <td className="text-transparent">.</td>
              <td className="text-transparent">.</td>
              <td className="text-transparent">.</td>
              {showTax && <td className="text-transparent">.</td>}
              <td className="text-transparent">.</td>
            </tr>

            {/* Totals strip */}
            {Number(doc.extra_discount_amount || 0) > 0 && (
              <tr>
                <td colSpan={showTax ? 6 : 5} className="border-r-0 border-l-0 border-b-0"></td>
                <td className="font-semibold text-right">Less: Extra Discount{doc.extra_discount_type === "pct" ? ` (${Number(doc.extra_discount_value || 0)}%)` : ""}</td>
                <td className="text-right tabular">- {fmtINR(doc.extra_discount_amount)}</td>
              </tr>
            )}
            <tr>
              <td colSpan={showTax ? 6 : 5} className="border-r-0 border-l-0 border-b-0"></td>
              <td className="font-bold text-right">Total Taxable</td>
              <td className="text-right tabular font-bold">{fmtINR(doc.total_taxable)}</td>
            </tr>
            {showTax && (isInter ? (
              <tr>
                <td colSpan={showTax ? 6 : 5} className="border-r-0 border-l-0 border-b-0"></td>
                <td className="text-right">IGST</td>
                <td className="text-right tabular">{fmtINR(doc.total_igst)}</td>
              </tr>
            ) : (
              <>
                <tr>
                  <td colSpan={showTax ? 6 : 5} className="border-r-0 border-l-0 border-b-0"></td>
                  <td className="text-right">CGST</td>
                  <td className="text-right tabular">{fmtINR(doc.total_cgst)}</td>
                </tr>
                <tr>
                  <td colSpan={showTax ? 6 : 5} className="border-r-0 border-l-0 border-b-0"></td>
                  <td className="text-right">SGST</td>
                  <td className="text-right tabular">{fmtINR(doc.total_sgst)}</td>
                </tr>
              </>
            ))}
            {Math.abs(Number(doc.round_off || 0)) > 0.0001 && (
              <tr>
                <td colSpan={showTax ? 6 : 5} className="border-r-0 border-l-0 border-b-0"></td>
                <td className="text-right">Round Off</td>
                <td className="text-right tabular">{Number(doc.round_off) >= 0 ? "+ " : "- "}{fmtINR(Math.abs(Number(doc.round_off || 0)))}</td>
              </tr>
            )}
            {showAdvanceOnPrint && (
              <tr>
                <td colSpan={showTax ? 6 : 5} className="border-r-0 border-l-0 border-b-0"></td>
                <td className="text-right">Less: Advance Adjusted</td>
                <td className="text-right tabular">- {fmtINR(doc.advance_amount)}</td>
              </tr>
            )}
            <tr className="bg-[#FAF6E8]">
              <td colSpan={showTax ? 6 : 5} className="border-r-0 border-l-0 border-b-0"></td>
              <td className="text-right font-bold">{showAdvanceOnPrint ? "Balance Payable" : "Grand Total"}</td>
              <td className="text-right tabular font-bold">{fmtINR(grandPayable)}</td>
            </tr>
          </tbody>
        </table>

        {/* Amount in words */}
        <div className="border-t border-black p-2 text-[10px]">
          <span className="font-bold">Amount Chargeable (in words): </span>{numToWords(grandPayable)}
          <span className="float-right text-[9px] italic">E. & O.E.</span>
        </div>

        {/* HSN/SAC tax summary table */}
        {showTax && hsnRows.length > 0 && (
          <table className="w-full text-[10px]">
            <thead>
              <tr className="bg-[#FAF6E8]">
                <th className="text-left">HSN/SAC</th>
                <th className="!text-right">Taxable Value</th>
                {isInter ? (
                  <>
                    <th className="!text-right">IGST Rate</th>
                    <th className="!text-right">IGST Amount</th>
                  </>
                ) : (
                  <>
                    <th className="!text-right">CGST Rate</th>
                    <th className="!text-right">CGST Amount</th>
                    <th className="!text-right">SGST Rate</th>
                    <th className="!text-right">SGST Amount</th>
                  </>
                )}
                <th className="!text-right">Tax Amount</th>
              </tr>
            </thead>
            <tbody>
              {hsnRows.map(h => {
                const tax = (h.cgst || 0) + (h.sgst || 0) + (h.igst || 0);
                return (
                  <tr key={h.hsn}>
                    <td className="font-mono">{h.hsn}</td>
                    <td className="text-right tabular">{fmtINR(h.taxable)}</td>
                    {isInter ? (
                      <>
                        <td className="text-right tabular">{h.igst_rate.toFixed(0)}%</td>
                        <td className="text-right tabular">{fmtINR(h.igst)}</td>
                      </>
                    ) : (
                      <>
                        <td className="text-right tabular">{h.cgst_rate.toFixed(2)}%</td>
                        <td className="text-right tabular">{fmtINR(h.cgst)}</td>
                        <td className="text-right tabular">{h.sgst_rate.toFixed(2)}%</td>
                        <td className="text-right tabular">{fmtINR(h.sgst)}</td>
                      </>
                    )}
                    <td className="text-right tabular font-semibold">{fmtINR(tax)}</td>
                  </tr>
                );
              })}
              <tr className="bg-[#FAF6E8] font-bold">
                <td>Total</td>
                <td className="text-right tabular">{fmtINR(doc.total_taxable)}</td>
                {isInter ? (
                  <>
                    <td></td>
                    <td className="text-right tabular">{fmtINR(doc.total_igst)}</td>
                  </>
                ) : (
                  <>
                    <td></td>
                    <td className="text-right tabular">{fmtINR(doc.total_cgst)}</td>
                    <td></td>
                    <td className="text-right tabular">{fmtINR(doc.total_sgst)}</td>
                  </>
                )}
                <td className="text-right tabular">{fmtINR((doc.total_cgst || 0) + (doc.total_sgst || 0) + (doc.total_igst || 0))}</td>
              </tr>
            </tbody>
          </table>
        )}

        {/* Tax in words */}
        {showTax && (
          <div className="border-t border-black p-2 text-[10px]">
            <span className="font-bold">Tax Amount (in words): </span>{numToWords((doc.total_cgst || 0) + (doc.total_sgst || 0) + (doc.total_igst || 0))}
          </div>
        )}

        {/* Bank + Declaration + Signature */}
        <div className="flex border-t-2 border-black">
          <div className="w-1/2 p-2.5 border-r-2 border-black text-[10px]">
            <div className="font-bold uppercase mb-1">Company's Bank Details</div>
            <div><b>A/c Holder's Name:</b> {company.legal_name}</div>
            <div><b>Bank Name:</b> {company.bank_name || "—"}</div>
            <div><b>A/c No.:</b> <span className="font-mono">{company.bank_account || "—"}</span></div>
            <div><b>Branch:</b> {company.bank_branch || "—"}</div>
            <div><b>IFSC Code:</b> <span className="font-mono">{company.bank_ifsc || "—"}</span></div>
          </div>
          <div className="w-1/2 p-2.5 text-[10px] flex flex-col justify-between">
            <div>
              <div className="font-bold uppercase mb-1">Declaration</div>
              <div className="text-[9.5px] leading-snug">We declare that this invoice shows the actual price of the goods described and that all particulars are true and correct.</div>
            </div>
            <div className="mt-8 text-right">
              <div className="font-bold">for {company.legal_name}</div>
              <div className="border-t border-black inline-block min-w-[200px] mt-12 pt-1 text-[10px]">Authorised Signatory</div>
            </div>
          </div>
        </div>

        {/* Terms & Conditions — fixed footer */}
        <div className="border-t-2 border-black p-2.5 text-[10px]">
          <div className="font-bold uppercase mb-1">Terms &amp; Conditions</div>
          <ol className="list-decimal pl-5 space-y-0.5 text-[10px] leading-snug">
            {STANDARD_TERMS.map((t) => <li key={t}>{t}</li>)}
            {(doc.terms || company.terms) && <li className="font-medium">{doc.terms || company.terms}</li>}
          </ol>
        </div>

        {doc.notes && (
          <div className="border-t border-black p-2 text-[10px]">
            <span className="font-bold">Notes: </span>{doc.notes}
          </div>
        )}
      </div>
    </div>
  );
}

function Cell({ label, value, last }) {
  return (
    <div className={`px-2 py-1 border-black ${last ? "" : "border-b"} ${"border-r"} text-[10px]`}>
      <div className="text-[8px] uppercase opacity-70">{label}</div>
      <div className="font-semibold leading-tight">{value || "—"}</div>
    </div>
  );
}

function MetaCell({ k, v, mono }) {
  return (
    <div className="px-2 py-1 border-r border-black last:border-r-0">
      <div className="text-[8px] uppercase opacity-70">{k}</div>
      <div className={`font-semibold ${mono ? "font-mono" : ""}`}>{v}</div>
    </div>
  );
}
